import React, { useEffect, useMemo, useRef, useState } from 'react';
import { message, Spin } from 'antd';
import { savePayerRequirements, saveIrsLetter, getCredPayoutAccount } from 'api/credentialing';
import type {
    AdditionalW9Item,
    IrsLetterDto,
    PayerRequirementsDto,
    TSelectedPayer,
} from 'components/CredentialingPayerSteps';
import {
    EPayerStep,
    GovProgramStep,
    ServiceAddressStep,
    OptumW9Step,
    AetnaFocusStep,
    IrsLetterStep,
    IrsLetterView,
    MedicaidEnrollmentFields,
    MedicaidEnrollmentView,
    entityHasAgreement,
    ServiceAddressView,
    GovProgramView,
    AetnaFocusView,
    AdditionalW9View,
    govComplete,
    serviceAddressComplete,
    additionalW9Complete,
    aetnaComplete,
    irsLetterComplete,
    medicareNotEnrolledAnywhere,
    seedAdditionalW9,
    mergeSeededW9,
    hasPrimaryW9,
    selectedStates,
} from 'components/CredentialingPayerSteps';
import useCredentialingPanels from 'hooks/credentialing/useCredentialingPanels';
import useCredentialInfo from 'hooks/credentialing/useCredentialInfo';
import usePayerOptions from 'hooks/credentialing/usePayerOptions';
import { ENPIType } from 'types/credentialing';
import ReviewSection from '../ReviewSection';
import {
    MEDICAID_SECTION,
    PAYER_SECTION_TITLES,
    type TSectionKey,
    type TIrsState,
    type TMcdCtx,
    loadPayerReqData,
    payerSectionScope,
    payerRequirementGaps,
} from './gaps';
import type { TActionFocus } from '../../actionFocus';
import s from '../../s.module.less';

interface Props {
    // 数据归属的 provider（group 随切换器变化；solo 传自己）。为空时不拉取。
    providerId?: number | string | null;
    // 宿主页面有 77px 固定底部导航时传 true（review 页）；弹窗内不传
    footerOffset?: boolean;
    // Settings 弹窗传 true：编辑态 Cancel/Save 在吸顶标题行（review 页不传，保持吸底条）
    actionsInHeader?: boolean;
    // action needed 深链定位（ctaPayload 协议）：按 fieldKey 前缀展开对应 section 编辑并
    // 选中目标州；state 不在「触发 ∪ 已存」并集里时（如 payer 不支持的州）注入州选项供补录
    initialFocus?: TActionFocus | null;
    // 触发计算用的选中 payer；不传时取该 provider 的 active panel（Settings）。group review 时 panel
    // 还没创建，由宿主传 pending 的 payer 选择
    payers?: TSelectedPayer[];
    // review Submit 后：没填完的板块标红
    hasErrors?: boolean;
    // review Submit 拦下时要展开编辑的板块（nonce 变化才重新展开一次）
    attention?: { step: TSectionKey; nonce: number } | null;
    onEditingChange?: (editing: boolean) => void;
    onSaved?: (providerId: string) => void;
}

// fieldKey 前缀 → payer-requirements 的 section

const FOCUS_PREFIX_TO_STEP: Record<string, TSectionKey> = {
    serviceAddresses: EPayerStep.SERVICE_ADDRESS,
    medicare: EPayerStep.GOV_PROGRAM,
    medicaid: EPayerStep.GOV_PROGRAM,
    medicareManagement: EPayerStep.GOV_PROGRAM,
    irsLetter: EPayerStep.IRS_LETTER,
    medicaidEnrollment: MEDICAID_SECTION,
    additionalW9: EPayerStep.OPTUM_W9,
    focusAreas: EPayerStep.AETNA_FOCUS,
};

const normTin = (v?: string) => (v || '').replace(/-/g, '').trim();

// payer 触发四步（Service address / Gov program / Aetna Focus / Optum W-9）的信息板块组，
// section 按"该 provider 有数据才显示"渲染，Edit 内联编辑（快照回滚 + 校验后全量保存）。
// group review 页与 Settings 弹窗的 Payer requirements tab 共用。
export default function PayerRequirementsPanel({
    providerId,
    footerOffset,
    actionsInHeader,
    initialFocus,
    payers,
    hasErrors,
    attention,
    onEditingChange,
    onSaved,
}: Props) {
    const [reqsByProvider, setReqsByProvider] = useState<Record<string, PayerRequirementsDto>>({});
    // 该 provider 的 active panel 用于触发计算（review 页场景 panel 尚未创建，列表为空，
    // 州列表自然回落到已存数据——与旧行为一致）
    const [panelRows] = useCredentialingPanels();
    const [loading, setLoading] = useState(false);
    const [editingStep, setEditingStep] = useState<TSectionKey | null>(null);
    const [saving, setSaving] = useState(false);
    const snapshotRef = useRef<PayerRequirementsDto | null>(null);
    // IRS letter 走独立接口（provider 级）；billsUnderTaxId 决定是否该问（solo 看 Verification 的 TIN ≠ SSN）
    const [irsByProvider, setIrsByProvider] = useState<Record<string, TIrsState>>({});
    const irsSnapshotRef = useRef<IrsLetterDto | null>(null);
    const [credentialInfo] = useCredentialInfo();
    const isGroupPractice = credentialInfo.billingNpiType?.value === ENPIType.PRACTICE_NPI;
    // Medicaid 问卷：该 provider 的实体类型 + 预填依据（Disclosures 执照题答 No）；payout 是 practice / 本人一份
    const [mcdCtxByProvider, setMcdCtxByProvider] = useState<Record<string, TMcdCtx>>({});
    const [payoutOnFile, setPayoutOnFile] = useState(false);
    useEffect(() => {
        getCredPayoutAccount()
            .then((res: any) => setPayoutOnFile(!!res?.data?.data?.accountNumber))
            .catch(() => undefined);
    }, []);

    const pid = providerId != null ? String(providerId) : '';
    const reqs = (pid && reqsByProvider[pid]) || {};
    const irs = (pid && irsByProvider[pid]) || { letter: {}, billsUnderTaxId: false };

    // 按 provider 懒加载 + 缓存；切 provider 重置编辑态
    useEffect(() => {
        setEditingStep(null);
        if (!pid || reqsByProvider[pid]) return undefined;
        let cancelled = false;
        setLoading(true);
        (async () => {
            try {
                const data = await loadPayerReqData(pid, isGroupPractice);
                if (!cancelled) {
                    setMcdCtxByProvider((prev) => ({ ...prev, [pid]: data.mcdCtx }));
                    setReqsByProvider((prev) => ({ ...prev, [pid]: data.reqs }));
                    setIrsByProvider((prev) => ({ ...prev, [pid]: data.irs }));
                }
            } catch (e) {
                if (!cancelled) setReqsByProvider((prev) => ({ ...prev, [pid]: {} }));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pid]);

    const patchReqs = (patch: Partial<PayerRequirementsDto>) => {
        if (!pid) return;
        setReqsByProvider((prev) => ({ ...prev, [pid]: { ...(prev[pid] || {}), ...patch } }));
    };

    // W-9 板块进入编辑且尚无 primary：从该 provider 的 Verification 种一条（仅首次；深链注入的
    // other 条目保留，种子只补 primary 位；Cancel 随快照回滚，下次打开重种）
    const maybeSeedW9 = () => {
        if (!pid) return;
        const cur = reqsByProvider[pid]?.additionalW9;
        if (hasPrimaryW9(cur)) return;
        seedAdditionalW9(cur, pid).then((seeded) => {
            if (!seeded) return;
            setReqsByProvider((prev) => {
                const dto = prev[pid] || {};
                return { ...prev, [pid]: { ...dto, additionalW9: mergeSeededW9(dto.additionalW9, seeded) } };
            });
        });
    };

    const patchIrs = (letter: IrsLetterDto) => {
        if (!pid) return;
        setIrsByProvider((prev) => ({ ...prev, [pid]: { ...(prev[pid] || { billsUnderTaxId: false }), letter } }));
    };

    const openEdit = (step: TSectionKey) => {
        if (!pid) return;
        snapshotRef.current = reqsByProvider[pid] || {};
        irsSnapshotRef.current = irsByProvider[pid]?.letter || {};
        setEditingStep(step);
        if (step === EPayerStep.OPTUM_W9) maybeSeedW9();
    };

    // 深链：该 provider 数据就位后自动展开目标 section 编辑（一次性，换 focus/provider 重置）
    const focusDoneRef = useRef(false);
    useEffect(() => {
        focusDoneRef.current = false;
    }, [initialFocus, pid]);
    useEffect(() => {
        if (!initialFocus || focusDoneRef.current || !pid || !reqsByProvider[pid]) return;
        const step = FOCUS_PREFIX_TO_STEP[initialFocus.fieldKey.split('.')[0]];
        if (step === undefined) return;
        focusDoneRef.current = true;
        snapshotRef.current = reqsByProvider[pid] || {};
        irsSnapshotRef.current = irsByProvider[pid]?.letter || {};
        setEditingStep(step);
        if (step === EPayerStep.OPTUM_W9) maybeSeedW9();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialFocus, pid, reqsByProvider]);

    // review Submit 拦下：该 provider 数据就位后展开指定板块（每个 nonce 一次）
    const attentionDoneRef = useRef<number | null>(null);
    useEffect(() => {
        if (!attention || attentionDoneRef.current === attention.nonce || !pid || !reqsByProvider[pid]) return;
        attentionDoneRef.current = attention.nonce;
        openEdit(attention.step);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attention, pid, reqsByProvider]);

    useEffect(() => {
        onEditingChange?.(editingStep !== null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editingStep]);

    const cancelEdit = () => {
        if (pid && snapshotRef.current) {
            const snapshot = snapshotRef.current;
            setReqsByProvider((prev) => ({ ...prev, [pid]: snapshot }));
        }
        if (pid && irsSnapshotRef.current) patchIrs(irsSnapshotRef.current);
        setEditingStep(null);
    };

    const saveSection = async (validate: () => boolean, step?: TSectionKey) => {
        if (!pid) return;
        if (!validate()) {
            message.error('Please complete the required fields before saving.');
            return;
        }
        setSaving(true);
        try {
            const res: any =
                step === EPayerStep.IRS_LETTER
                    ? await saveIrsLetter(irsByProvider[pid]?.letter || {}, pid)
                    : await savePayerRequirements(reqsByProvider[pid] || {}, pid);
            if (res?.error) throw new Error(res.error);
            setEditingStep(null);
            message.success('Changes saved.');
            onSaved?.(pid);
        } catch (e) {
            message.error('Failed to save changes. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    // 州列表按「当前 active panel 的触发计算 ∪ 已存数据」得出（与 onboarding / Add payers 链同一套
    // 触发函数）：新加了 panel 但还没存过 requirements 的州也会出现（空表单待补，保存时校验补全）；
    // 已存数据里有、但 panel 已不触发的州（如 payer 已撤回）继续展示，不丢数据。
    // panel 行没有 payerType：按 klarityPayerId 去 payer 列表反查（Blue plan 靠它识别，IRS letter 要用）
    const activeRows = useMemo(
        () => panelRows.filter((r) => String(r.providerId) === pid && r.status !== 'WITHDRAWN'),
        [panelRows, pid],
    );
    const rowStates = useMemo(() => Array.from(new Set(activeRows.map((r) => r.payerState))), [activeRows]);
    const [payerOptions] = usePayerOptions(rowStates, rowStates.length > 0);
    const panelPayers = useMemo<TSelectedPayer[]>(
        () =>
            activeRows.map((r) => ({
                payerName: r.payerName,
                state: r.payerState,
                payerType: payerOptions.find((p) => p.id === r.klarityPayerId)?.payerType,
            })),
        [activeRows, payerOptions],
    );
    const activePayers = payers || panelPayers;
    const uniq = (arr: string[]) => Array.from(new Set(arr));
    const scope = payerSectionScope(activePayers, reqs, irs);
    const gaps = hasErrors ? payerRequirementGaps(activePayers, { reqs, irs }) : [];

    // 深链定位的州注入：action item 指到的州可能既不在触发集也没存过行（payer 不支持的州），
    // 把它并进对应 section 的州列表，渲染空块供补录
    const focusPrefix = initialFocus?.fieldKey.split('.')[0];
    // 没带州（引擎的 Medicare/Medicaid 参与度 ask 不带 state）时，指到的 section 按当前所有 panel 的州展开：
    // BSC / Optum / Kaiser 这类 panel 不触发 Gov 步骤，没这条 section 整个隐藏、ask 就没有输入框可填
    const focusState = (prefixes: string[]) => {
        if (!focusPrefix || !prefixes.includes(focusPrefix)) return [];
        return initialFocus?.state ? [initialFocus.state] : selectedStates(activePayers);
    };

    const svcStates = uniq([...scope.svcStates, ...focusState(['serviceAddresses'])]);
    const govStates = uniq([...scope.govStates, ...focusState(['medicare', 'medicaid'])]);

    // W-9（09/01 重构）：无州维度，show = UHC 触发 ∪ 已存数据 ∪ 深链指到；深链条目
    // 未录入过时合成占位行（对齐旧协议里 focus state 不在已存数据的处理），编辑态由
    // OptumW9Step 按 focusEntries 追加预填条目卡补录。
    // 新协议 optumW9Required 给 MISSING 清单（tin + entityName 预填），旧协议退回单 taxId
    const w9SavedItems = reqs.additionalW9?.items || [];
    const w9FocusEntries: { taxId: string; nameOnW9?: string }[] =
        focusPrefix === 'additionalW9'
            ? initialFocus?.w9Missing?.map((e) => ({ taxId: e.taxId, nameOnW9: e.entityName })) ||
              (initialFocus?.taxId ? [{ taxId: initialFocus.taxId }] : [])
            : [];
    const w9Show = scope.w9Show || w9FocusEntries.length > 0;
    const w9ViewItems: AdditionalW9Item[] = [
        ...w9SavedItems,
        ...w9FocusEntries.filter((e) => !w9SavedItems.some((it) => normTin(it.taxId) === normTin(e.taxId))),
    ];
    // 触发应有但一条没存过：合成空 primary 占位行，视图里显示 Not provided
    const w9ViewItemsFinal = w9ViewItems.length ? w9ViewItems : [{ primary: true } as AdditionalW9Item];

    // 只读视图按并集州渲染：没存过行的州合成 { state } 占位行，视图里显示 Not provided
    // （否则旧数据全空时 section 只剩标题 + Edit，正文一片空白）
    const withPlaceholders = <T extends { state: string }>(states: string[], rows: T[]): T[] =>
        states.map((st) => rows.find((r) => r.state === st) || ({ state: st } as T));
    const svcViewItems = withPlaceholders(svcStates, reqs.serviceAddresses || []);
    const medicareViewItems = withPlaceholders(govStates, reqs.medicare || []);
    const medicaidViewItems = withPlaceholders(govStates, reqs.medicaid || []);
    // managing employee 只在任一州 Medicare 未注册时问（与链里同一口径）
    const askManagement = medicareNotEnrolledAnywhere(govStates, reqs.medicare || []);
    // Medicaid 答了 No 的州：Klarity 代为注册，问卷答案在单独的板块里看和改（原型 mcdProviderSettingsHTML）
    const mcdNoStates = govStates.filter((st) => (reqs.medicaid || []).find((m) => m.state === st)?.enrolled === false);
    const mcdVariant = isGroupPractice && credentialInfo.providers.length > 1 ? 'rendering' : 'individual';
    const mcdCtx = mcdCtxByProvider[pid] || { licenseActionNo: false };
    const irsShow = scope.irsShow || focusPrefix === 'irsLetter';

    const sections: {
        step: TSectionKey;
        show: boolean;
        validate: () => boolean;
        view: React.ReactNode;
        edit: React.ReactNode;
    }[] = [
        {
            step: EPayerStep.SERVICE_ADDRESS,
            show: svcStates.length > 0,
            validate: () => serviceAddressComplete(svcStates, reqs.serviceAddresses || []),
            view: <ServiceAddressView items={svcViewItems} />,
            edit: (
                <ServiceAddressStep
                    focusState={initialFocus?.state}
                    states={svcStates}
                    value={reqs.serviceAddresses || []}
                    onChange={(serviceAddresses) => patchReqs({ serviceAddresses })}
                    onContinue={() => undefined}
                    hideFooter
                />
            ),
        },
        {
            step: EPayerStep.GOV_PROGRAM,
            show: govStates.length > 0,
            validate: () =>
                govComplete(govStates, reqs.medicare || [], reqs.medicaid || [], reqs.medicareManagement ?? null),
            view: (
                <GovProgramView
                    medicare={medicareViewItems}
                    medicaid={medicaidViewItems}
                    management={askManagement ? (reqs.medicareManagement ?? null) : false}
                />
            ),
            edit: (
                <GovProgramStep
                    focusState={initialFocus?.state}
                    states={govStates}
                    medicare={reqs.medicare || []}
                    medicaid={reqs.medicaid || []}
                    onChange={(medicare, medicaid) => patchReqs({ medicare, medicaid })}
                    management={reqs.medicareManagement}
                    onManagementChange={(medicareManagement) => patchReqs({ medicareManagement })}
                    medicaidInline={false}
                    onContinue={() => undefined}
                    hideFooter
                />
            ),
        },
        {
            step: MEDICAID_SECTION,
            show: mcdNoStates.length > 0,
            // 原型：问卷不拦保存（Klarity 代为注册，缺的由 ops 跟进）
            validate: () => true,
            view: (
                <MedicaidEnrollmentView
                    value={reqs.medicaidEnrollment}
                    variant={mcdVariant}
                    isCA={mcdNoStates.includes('CA')}
                    showOrgAgreement={entityHasAgreement(mcdCtx.entityType)}
                    enrollingIn={mcdNoStates}
                />
            ),
            edit: (
                <MedicaidEnrollmentFields
                    variant={mcdVariant}
                    isCA={mcdNoStates.includes('CA')}
                    showOrgAgreement={entityHasAgreement(mcdCtx.entityType)}
                    derive={{
                        licenseActionNo: mcdCtx.licenseActionNo,
                        everEnrolled: (reqs.medicaid || []).some((m) => m.enrolled === true),
                        payoutOnFile,
                    }}
                    value={reqs.medicaidEnrollment}
                    onChange={(medicaidEnrollment) => patchReqs({ medicaidEnrollment })}
                />
            ),
        },
        {
            step: EPayerStep.IRS_LETTER,
            show: irsShow,
            validate: () => irsLetterComplete(irs.letter),
            view: <IrsLetterView value={irs.letter} />,
            edit: <IrsLetterStep value={irs.letter} onChange={patchIrs} onContinue={() => undefined} hideFooter />,
        },
        {
            step: EPayerStep.AETNA_FOCUS,
            show: scope.aetnaShow || focusPrefix === 'focusAreas',
            validate: () => aetnaComplete(reqs.focusAreas || []),
            view: <AetnaFocusView focusAreas={reqs.focusAreas || []} />,
            edit: (
                <AetnaFocusStep
                    value={reqs.focusAreas || []}
                    onChange={(focusAreas) => patchReqs({ focusAreas })}
                    onContinue={() => undefined}
                    hideFooter
                />
            ),
        },
        {
            step: EPayerStep.OPTUM_W9,
            show: w9Show,
            validate: () => additionalW9Complete(reqs.additionalW9),
            view: <AdditionalW9View items={w9ViewItemsFinal} />,
            edit: (
                <OptumW9Step
                    focusEntries={w9FocusEntries}
                    value={reqs.additionalW9 || {}}
                    onChange={(additionalW9) => patchReqs({ additionalW9 })}
                    onContinue={() => undefined}
                    hideFooter
                />
            ),
        },
    ];

    const visible = sections.filter((sec) => sec.show);

    return (
        // 加载中给初始高度：避免 tab 内容从 0 高度突然撑开，spinner 也有落点
        <Spin spinning={loading}>
            <div style={{ minHeight: loading ? 240 : undefined }}>
                {visible.map((sec) => (
                    <ReviewSection
                        key={sec.step}
                        sectionKey={sec.step}
                        title={PAYER_SECTION_TITLES[sec.step]}
                        hasErrors={gaps.includes(sec.step)}
                        viewContent={sec.view}
                        editing={editingStep === sec.step}
                        saving={saving}
                        footerOffset={footerOffset}
                        actionsInHeader={actionsInHeader}
                        onEdit={() => openEdit(sec.step)}
                        onCancel={cancelEdit}
                        onSave={() => saveSection(sec.validate, sec.step)}
                    >
                        {sec.edit}
                    </ReviewSection>
                ))}
                {/* 空态：该 provider 的 payer 选择没有触发任何 requirement（原型 payer-info tab 同款文案） */}
                {!loading && visible.length === 0 && (
                    <div className={s.reviewSection}>
                        <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6 }}>
                            No additional payer-specific requirements for your current panels.
                        </div>
                    </div>
                )}
            </div>
        </Spin>
    );
}
